/**
 * Shared difficulty-level options for the child-level selectors.
 *
 * Single source of truth for the two <select> controls that let a parent pick a
 * child's learning level:
 *   - ChildProfilesGrid (existing child card — change level inline)
 *   - AddChildDialog (new child creation — pick level up-front)
 *
 * Each label maps to the internal DifficultyTier value stored verbatim on the
 * child (`difficultyTier`) and matched against content by exact equality — kept
 * in parity with the backend VALID_DIFFICULTY_TIERS set. Rendered in this exact
 * order; do not reorder/relabel (asserted by AC-TS007.5).
 *
 * P4/P5 are single top-level grade options; the three P6 bands live in the
 * 'Primary 6' <optgroup>. Read-time default is 'hard' (BR-TS001.2).
 */

import type { DifficultyTier } from '@/types/coins';

/** Top-level grade-level options rendered above the Primary 6 band group. */
export const GRADE_LEVEL_OPTIONS: Array<{ label: string; value: DifficultyTier }> = [
  { label: 'Primary 4', value: 'P4' },
  { label: 'Primary 5', value: 'P5' },
];

/**
 * The three Primary 6 difficulty bands (rendered inside the 'Primary 6' optgroup).
 *
 * Labels are deliberately NOT 'Foundation'/'Standard' — those are the names of
 * real MOE streams, and parents read them as a claim that a band is aligned to
 * the national Foundation/Standard syllabus. These bands are ThinkOtter's own
 * content difficulty scale and have no syllabus meaning, so the labels must not
 * borrow streaming vocabulary. Keep the backend twin (report_html_template.
 * DIFFICULTY_LABELS) in sync when changing these.
 */
export const P6_BAND_OPTIONS: Array<{ label: string; value: DifficultyTier }> = [
  { label: 'P6 Base', value: 'standard' },
  { label: 'P6 Intermediate', value: 'hard' },
  { label: 'P6 Advanced', value: 'beyond' },
];

/** Read-time default tier when a child has none set (BR-TS001.2). */
export const DEFAULT_DIFFICULTY_TIER: DifficultyTier = 'hard';

/**
 * Starting selection for the FIRST child a parent creates (AddFirstChildModal).
 *
 * Wayne, 2026-07-27: "for P6, default difficulty to the easiest level." A parent setting up
 * her very first child has no evidence about where her daughter sits, and the first session
 * is the one that decides whether she comes back. Starting at the easiest P6 band means the
 * first experience errs towards a win rather than a wall; the level is one dropdown away on
 * the child's card afterwards.
 *
 * ⚠️ DELIBERATELY SEPARATE from DEFAULT_DIFFICULTY_TIER above, which is the READ-TIME default
 * for a child who has no tier stored. Do not merge them: changing that one silently re-bands
 * every existing child who predates the level selector, with no audit trail and no way for a
 * parent to know their child's work just got easier.
 */
export const FIRST_CHILD_DEFAULT_DIFFICULTY_TIER: DifficultyTier = 'standard';
